import { create } from 'zustand';
import axios from 'axios';

interface User {
  id: string;
  username: string;
  email: string;
  apiToken: string;
  widgetSettings: WidgetSettings;
}

interface FontStyle {
  fontFamily: string;
  fontSize: number;
  color: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  letterSpacing: number;
  wordSpacing: number;
  textAlign: 'left' | 'center' | 'right';
  verticalAlign: 'top' | 'center' | 'bottom';
  textShadow: boolean;
  shadowColor: string;
  shadowBlur: number;
  shadowOffsetX: number;
  shadowOffsetY: number;
  outline: boolean;
  outlineColor: string;
  outlineWidth: number;
}

interface WidgetSettings {
  layout: 'image-top' | 'image-bottom' | 'image-left' | 'image-right' | 'image-background';
  bgColor: string;
  bgOpacity: number;
  borderRadius: number;
  padding: number;
  maxWidth: number;
  defaultImage: string;
  imageSize: number;
  imageBorderRadius: number;
  animationIn: 'fade' | 'slide' | 'bounce' | 'zoom' | 'flip' | 'none';
  animationOut: 'fade' | 'slide' | 'bounce' | 'zoom' | 'flip' | 'none';
  animationDuration: number;
  displayDuration: number;
  soundEnabled: boolean;
  soundUrl: string;
  minAmount: number;
  maxAmount: number;
  headerTemplate: string;
  messageTemplate: string;
  headerFont: FontStyle;
  messageFont: FontStyle;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center' | 'top-center' | 'bottom-center';
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  checkAuth: () => Promise<void>;
}

const api = axios.create({
  baseURL: '/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('token'),
  loading: false,

  login: async (email: string, password: string) => {
    set({ loading: true });
    try {
      const { data } = await api.post('/auth/login', { email, password });
      localStorage.setItem('token', data.token);
      set({ user: data.user, token: data.token, loading: false });
    } catch (error: any) {
      set({ loading: false });
      throw error.response?.data?.error || 'Login failed';
    }
  },

  register: async (username: string, email: string, password: string) => {
    set({ loading: true });
    try {
      const { data } = await api.post('/auth/register', { username, email, password });
      localStorage.setItem('token', data.token);
      set({ user: data.user, token: data.token, loading: false });
    } catch (error: any) {
      set({ loading: false });
      throw error.response?.data?.error || 'Registration failed';
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    set({ user: null, token: null });
  },

  updateUser: (user: User) => {
    set({ user });
  },

  checkAuth: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      set({ user: null, token: null });
      return;
    }
    
    try {
      const { data } = await api.get('/auth/me');
      set({ user: data.user, token });
    } catch {
      localStorage.removeItem('token');
      set({ user: null, token: null });
    }
  },
}));

export const apiClient = api;
export type { User, WidgetSettings, FontStyle };
